import * as React from "react";
import {
	Alert,
	Box,
	Button,
	Checkbox,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	Stack,
	Tab,
	Tabs,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import { SparkleIcon } from "@phosphor-icons/react/dist/ssr/Sparkle";

import { useAgentsList } from "@/hooks/api/use-agents";
import { DraftKind, useAcceptDrafts, useGenerateDrafts } from "@/hooks/api/use-proving-ground";

/**
 * Draft, review, then save.
 *
 * The review step is the point. Generating straight into the library gives you a dozen
 * entries you then have to weed, and a weak caller is indistinguishable from a good one
 * until a run has already used it and the results are quietly wrong.
 */
export function GenerateDialog({
	kind,
	open,
	onClose,
	onSaved,
}: {
	kind: DraftKind;
	open: boolean;
	onClose: () => void;
	onSaved: () => void;
}): React.JSX.Element {
	const generate = useGenerateDrafts(kind);
	const accept = useAcceptDrafts(kind);
	const agents = useAgentsList();

	const [source, setSource] = React.useState<"describe" | "agent">("agent");
	const [instruction, setInstruction] = React.useState("");
	const [agentId, setAgentId] = React.useState("");
	const [count, setCount] = React.useState(6);
	const [drafts, setDrafts] = React.useState<Record<string, any>[] | null>(null);
	const [keep, setKeep] = React.useState<Set<number>>(new Set());

	const agentList = agents.data?.data ?? [];

	React.useEffect(() => {
		if (open) return;
		setDrafts(null);
		setKeep(new Set());
		setInstruction("");
		setAgentId("");
		setSource("agent");
	}, [open]);

	const noun = kind === "personas" ? "caller" : kind === "scenarios" ? "scenario" : "check";

	const placeholder =
		kind === "personas"
			? "Malaysian SME owners calling about lead generation. A couple should code-switch between English and Malay mid-sentence, one should be on a bad line in a car, one should be hostile from the first word, one should barely speak."
			: "Situations a price-sensitive buyer puts an outbound sales agent through: refusing to be qualified before hearing a number, demanding a human, asking to be taken off the list, and arriving already annoyed about a previous call.";

	const fromAgent = source === "agent";
	const canDraft = fromAgent ? Boolean(agentId) : instruction.trim().length >= 10;

	const draft = async () => {
		const result = await generate.mutateAsync({
			instruction,
			count,
			...(fromAgent ? { agentId } : {}),
		});
		setDrafts(result);
		setKeep(new Set(result.map((_, index) => index)));
	};

	const save = async () => {
		await accept.mutateAsync({
			drafts: (drafts ?? []).filter((_, index) => keep.has(index)),
			...(fromAgent && agentId ? { agentId } : {}),
		});
		onClose();
		onSaved();
	};

	const toggle = (index: number) => {
		setKeep((current) => {
			const next = new Set(current);
			if (next.has(index)) next.delete(index);
			else next.add(index);
			return next;
		});
	};

	/**
	 * A caller carries an objective it should not.
	 *
	 * Every caller is run against many situations, and the situation supplies what they are
	 * after. A caller who also has a goal fights whichever situation it is paired with, so a
	 * share of the grid quietly becomes self-contradictory and the agent is blamed for it.
	 * The generator is told this at length and mostly complies; this catches the rest at the
	 * one point where a person is already reading the drafts.
	 */
	const carriesGoal = (item: Record<string, any>): boolean => {
		if (kind !== "personas") return false;
		return /\b(you will (ask|refuse|insist|demand|push|not (agree|answer|book))|you want (to|a|the)|you are (looking for|trying to|testing (if|whether))|refuse to|until they|before you (agree|book|proceed))\b/i.test(
			String(item.prompt ?? "")
		);
	};

	const flagged = (drafts ?? []).filter((item) => carriesGoal(item)).length;

	return (
		<Dialog open={open} onClose={generate.isPending || accept.isPending ? undefined : onClose} maxWidth="md" fullWidth>
			<DialogTitle>
				{drafts
					? `Review drafts (${keep.size} of ${drafts.length} selected)`
					: kind === "metrics"
						? "Generate scorecard checks"
						: `Generate ${kind}`}
			</DialogTitle>
			<DialogContent>
				{drafts ? (
					<Stack spacing={1.5} sx={{ mt: 1 }}>
						<Alert severity="info">
							Nothing is saved yet. Untick anything weak — a poor {noun} is indistinguishable from a good one once
							results start coming back.
						</Alert>
						{flagged > 0 ? (
							<Alert severity="warning">
								{flagged === 1 ? "One caller looks like it carries" : `${flagged} callers look like they carry`} a goal
								— something they want or will refuse. That belongs in a scenario. A caller who has its own agenda fights
								whichever scenario it is paired with, and the agent gets blamed for the argument. Edit it after saving,
								or untick and draft again.
							</Alert>
						) : null}
						{drafts.map((item, index) => (
							<Box
								key={index}
								onClick={() => toggle(index)}
								sx={{
									display: "flex",
									gap: 1,
									p: 1.5,
									borderRadius: 1,
									border: 1,
									borderColor: keep.has(index) ? "primary.main" : "divider",
									cursor: "pointer",
									opacity: keep.has(index) ? 1 : 0.55,
								}}
							>
								<Checkbox size="small" checked={keep.has(index)} sx={{ p: 0, alignSelf: "flex-start", mt: 0.25 }} />
								<Box sx={{ minWidth: 0 }}>
									<Typography variant="body2" sx={{ fontWeight: 600 }}>
										{item.name}
									</Typography>
									{item.description ? (
										<Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
											{item.description}
										</Typography>
									) : null}
									<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
										{item.prompt ?? item.situation ?? item.definition}
									</Typography>
									<Stack direction="row" spacing={0.5} sx={{ mt: 0.75, flexWrap: "wrap" }} useFlexGap>
										{(item.languages ?? []).map((language: string) => (
											<Chip key={language} size="small" variant="outlined" label={language} />
										))}
										{item.codeSwitch ? <Chip size="small" color="primary" label="Code-switches" /> : null}
										{item.contact?.Name ? (
											<Chip size="small" variant="outlined" label={`Contact: ${item.contact.Name}`} />
										) : null}
										{item.kind ? (
											<Chip
												size="small"
												variant="outlined"
												color={item.kind === "llm_judge" ? "default" : "success"}
												label={
													item.kind === "llm_judge"
														? "LLM judge"
														: item.kind === "deterministic"
															? "Pattern"
															: `${item.config?.measure ?? "measure"} ${item.config?.operator ?? ""} ${item.config?.threshold ?? ""}`
												}
											/>
										) : null}
										{item.mode === "voice_only" ? <Chip size="small" variant="outlined" label="Voice only" /> : null}
										{carriesGoal(item) ? (
											<Tooltip title="Reads like it wants something. A caller should have a manner, not an agenda — what they want comes from the scenario.">
												<Chip size="small" color="warning" variant="outlined" label="Carries a goal" />
											</Tooltip>
										) : null}
									</Stack>
								</Box>
							</Box>
						))}
					</Stack>
				) : (
					<Stack spacing={2.5} sx={{ mt: 1 }}>
						<Tabs value={source} onChange={(_event, value: "describe" | "agent") => setSource(value)}>
							<Tab value="agent" label="From an agent" sx={{ minHeight: "auto" }} />
							<Tab value="describe" label="Describe them" sx={{ minHeight: "auto" }} />
						</Tabs>

						{fromAgent ? (
							<React.Fragment>
								<Alert severity="info">
									{kind === "personas"
										? "Reads the agent's own prompt and builds callers from the audience it actually calls."
										: kind === "scenarios"
											? "Reads the agent's own prompt and builds situations that walk into the branches it actually has — its call flow, the objections it was told to handle, and the places it has no instruction at all."
											: "Reads the agent's own prompt as a specification and turns it into checks — every rule it was told never to break, every disclosure it must make, every stage that has to happen."}
								</Alert>
								<FormControl fullWidth>
									<InputLabel>Agent</InputLabel>
									<Select value={agentId} label="Agent" onChange={(event) => setAgentId(event.target.value)}>
										{agentList.map((agent: any) => (
											<MenuItem key={agent.id} value={agent.id}>
												{agent.name}
											</MenuItem>
										))}
									</Select>
								</FormControl>
								<TextField
									label="Narrow it down (optional)"
									value={instruction}
									onChange={(event) => setInstruction(event.target.value)}
									placeholder={
										kind === "personas"
											? "Only Malay and Mandarin speakers, and make a couple of them code-switch."
											: "Focus on the consent gate, and anything the prompt has no answer for."
									}
									multiline
									minRows={2}
									fullWidth
								/>
							</React.Fragment>
						) : (
							<TextField
								label={`Describe the ${kind === "metrics" ? "checks" : kind} you want`}
								value={instruction}
								onChange={(event) => setInstruction(event.target.value)}
								placeholder={placeholder}
								multiline
								minRows={5}
								fullWidth
								autoFocus
							/>
						)}
						<FormControl sx={{ maxWidth: 160 }}>
							<InputLabel>How many</InputLabel>
							<Select value={count} label="How many" onChange={(event) => setCount(Number(event.target.value))}>
								{[3, 4, 6, 8, 10, 12].map((value) => (
									<MenuItem key={value} value={value}>
										{value}
									</MenuItem>
								))}
							</Select>
						</FormControl>
						<Typography variant="caption" color="text.secondary">
							Uses the model set as your judge in Proving Ground settings.
						</Typography>
					</Stack>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} color="secondary" disabled={generate.isPending || accept.isPending}>
					Cancel
				</Button>
				{drafts ? (
					<>
						<Button onClick={() => setDrafts(null)} color="secondary" disabled={accept.isPending}>
							Start over
						</Button>
						<Button onClick={save} variant="contained" disabled={keep.size === 0 || accept.isPending}>
							Create {keep.size}
						</Button>
					</>
				) : (
					<Button
						onClick={draft}
						variant="contained"
						startIcon={<SparkleIcon />}
						disabled={!canDraft || generate.isPending}
					>
						{generate.isPending ? "Drafting…" : "Draft"}
					</Button>
				)}
			</DialogActions>
		</Dialog>
	);
}
